<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import Modal from '@/components/ui/Modal.vue'
import Button from '@/components/ui/Button.vue'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/outline'
import { sessionExpiredState } from '@/composables/useSessionExpired'
import { computed } from 'vue'

defineProps<{
  open: boolean
  canRefresh: boolean
}>()

const emit = defineEmits<{
  (e: 'exit'): void
  (e: 'stay'): void
}>()

const { t } = useI18n()

const title = computed(() => t('auth.sessionExpired'))
const message = computed(() => t('auth.sessionExpiredMessage'))
const stayLabel = computed(() => t('auth.staySignedIn'))
const exitLabel = computed(() => t('auth.exitToLogin'))

const handleExit = () => {
  sessionExpiredState.choose('exit')
  emit('exit')
}

const handleStay = () => {
  sessionExpiredState.choose('stay')
  emit('stay')
}
</script>

<template>
  <Modal
    :open="open"
    :title="title"
    size="sm"
    :show-close="false"
    @close="handleExit"
  >
    <div class="sm:flex sm:items-start">
      <div
        class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-amber-100 sm:mx-0 sm:h-10 sm:w-10"
      >
        <ExclamationTriangleIcon
          class="h-6 w-6 text-amber-600"
          aria-hidden="true"
        />
      </div>
      <div class="mt-3 text-center sm:mt-0 sm:ms-4 sm:text-start">
        <p class="text-sm text-label">
          {{ message }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex w-full justify-end gap-3">
        <Button
          variant="secondary"
          @click="handleExit"
        >
          {{ exitLabel }}
        </Button>
        <Button
          v-if="canRefresh"
          variant="primary"
          @click="handleStay"
        >
          {{ stayLabel }}
        </Button>
      </div>
    </template>
  </Modal>
</template>
